import { useAppStore } from '@runtime/core/store/appStore'

// Token colors used throughout the UI.
// They can be overridden by the active POSTER theme.

export type Rgb = [number, number, number]
export const TOKEN_COLOR_KEYS = ['RED', 'BLU', 'YEL', 'GRN'] as const
export type TokenColorKey = (typeof TOKEN_COLOR_KEYS)[number]

export const COLOR_RGB: Record<TokenColorKey, Rgb> = {
  RED: [220, 80, 80],
  BLU: [80, 140, 230],
  YEL: [245, 210, 70],
  GRN: [120, 200, 90],
}

export function isTokenColorKey(value: string): value is TokenColorKey {
  return value in COLOR_RGB
}

export function getTokenColorRgb(
  color: string,
  fallback: TokenColorKey = 'RED',
): Rgb {
  return COLOR_RGB[isTokenColorKey(color) ? color : fallback]
}

function toRgb(hex: unknown): Rgb | null {
  const s = String(hex || '').trim()
  const m = /^#?([a-fA-F0-9]{6})$/.exec(s)
  if (!m) return null
  const h = m[1]
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ]
}

// Early boot: restore token colors from Zustand persisted store.
export const restorePersistedColors = () => {
  try {
    const arr = useAppStore.getState().themeTokens
    if (!Array.isArray(arr) || arr.length < 4) return

    const keys: TokenColorKey[] = ['RED', 'BLU', 'YEL', 'GRN']
    for (let i = 0; i < 4; i++) {
      const v = toRgb((arr as unknown[])[i])
      if (v) COLOR_RGB[keys[i]] = v
    }
  } catch {
    // ignore
  }
}

// Call at module load.
restorePersistedColors()

export const rgb = ([r, g, b]: Rgb) => `rgb(${r},${g},${b})`
export const luma = ([r, g, b]: Rgb) => 0.299 * r + 0.587 * g + 0.114 * b
